/**
 * International-orders shipping agreement.
 *
 * Loaded once from layout/theme.liquid, NOT from sections/main-cart-footer.
 * With settings.cart_type == 'drawer' the cart drawer is a checkout surface on
 * every page, and a script that only loaded on /cart left it with no shipping
 * agreement guard at all everywhere else. On /cart the form-level listener did
 * fire for the drawer's form, but the error message was injected into
 * #shipping-agreement inside main-cart-footer — behind the open drawer, where
 * the shopper saw nothing and concluded checkout was broken.
 *
 * Three things follow from the drawer being a live surface:
 *
 * 1. Every lookup is scoped to ONE surface. Both surfaces render an agreement
 *    and a .cart__checkout-button, and layout/theme.liquid renders the drawer
 *    before <main>, so a bare getElementById/querySelector resolves the
 *    drawer's copy even when the shopper is using the cart page's.
 * 2. Every listener is delegated from `document`. The agreement, its checkbox
 *    and both checkout buttons are re-rendered from Liquid by the Section
 *    Rendering API on each cart mutation, so anything bound directly to them
 *    would survive only until the first quantity change.
 * 3. No state the guard depends on may live inside the replaced markup. The
 *    shopper's tick is exactly such a state, and it is held below in a
 *    module-scoped record rather than read straight off a checkbox that is
 *    about to be thrown away.
 *
 * Visibility is decided server-side by snippets/cart-shipping-agreement.liquid,
 * which renders nothing for the domestic market. This file never toggles
 * display: a JS-applied `display: block` would be wiped by the drawer's next
 * re-render, leaving an international shopper blocked by a checkbox they could
 * no longer see.
 */
document.addEventListener('DOMContentLoaded', function () {
  const PAGE = { agreement: 'shipping-agreement', checkbox: 'shipping-policy-checkbox' };
  const DRAWER = {
    agreement: 'CartDrawer-shipping-agreement',
    checkbox: 'CartDrawer-shipping-policy-checkbox',
  };

  const CHECKOUT_SELECTOR = [
    '.cart__checkout-button',
    '.additional-checkout-buttons button',
    '.additional-checkout-buttons input[type="submit"]',
  ].join(', ');

  /** Which surface an element belongs to. */
  function surfaceFor(element) {
    return element && element.closest('cart-drawer') ? DRAWER : PAGE;
  }

  /** The surface a checkbox id belongs to, or null if it is not an agreement. */
  function surfaceForCheckboxId(id) {
    if (id === PAGE.checkbox) return PAGE;
    if (id === DRAWER.checkbox) return DRAWER;
    return null;
  }

  /**
   * THE SHOPPER'S AGREEMENT, KEPT OUTSIDE THE REPLACED MARKUP.
   *
   * Both agreements sit inside subtrees the Section Rendering API replaces —
   * #CartDrawer-shipping-agreement inside .drawer__inner, #shipping-agreement
   * inside the cart-footer section — and Liquid cannot re-render either of
   * them ticked, because the server never learns the shopper ticked anything.
   *
   * That cost international shoppers their first drawer checkout of every
   * session. Ticking #CartDrawer-shipping-policy-checkbox and clicking
   * #CartDrawer-Checkout passed the guard below; gift-manager.js's submit
   * handler then preventDefault()ed, refreshed the cart-drawer section — which
   * brought the checkbox back UNTICKED — and re-fired checkout on the fresh
   * button. The guard ran a second time, saw an unticked box, and refused a
   * checkout the shopper had already agreed to. The same failure fires on ANY
   * re-render landing between the tick and the click, a quantity change
   * included.
   *
   * So the tick is recorded here, in a variable no re-render can reach, and
   * re-applied to whichever checkbox comes back.
   *
   * PER SURFACE, NEVER SHARED — keyed by the surface's agreement id. The page
   * and the drawer carry two independent consents; copying one onto the other
   * would be accepting international shipping terms on the shopper's behalf,
   * on a surface they never looked at.
   *
   * IN MEMORY ONLY. Deliberately not localStorage/sessionStorage: consent is
   * preserved across a re-render within one view, and must never come back
   * pre-ticked for a shopper who has just arrived.
   *
   * That carry-over also spans an empty cart: tick it, empty the cart, add
   * items back — still the same view — and the tick reapplies to the new
   * cart's agreement without asking again. Accepted, not a defect: the terms
   * are static boilerplate rather than cart-specific, and the box comes back
   * visibly ticked so the shopper can untick it, which the listener above
   * does record (`!!target.checked` writes `false`).
   *
   * ONLY EVER RE-TICKS, never unticks. Unticked is the server-rendered
   * default, so this can never fight a fresh render.
   */
  const agreed = Object.create(null);

  /**
   * Delegated from `document` for the same reason every other listener here is
   * — the checkbox is replaced on each cart mutation, so a listener bound to it
   * would record one tick and then go silent. Capture phase because this only
   * ever reads, so it costs nothing and still sees the tick if something
   * downstream stops propagation.
   */
  document.addEventListener(
    'change',
    function (event) {
      const target = event.target;
      const surface = target && target.id ? surfaceForCheckboxId(target.id) : null;
      if (!surface) return;
      agreed[surface.agreement] = !!target.checked;
    },
    true
  );

  /**
   * Re-tick this surface's checkbox if the shopper already agreed on it.
   *
   * Assigning `.checked` is a property write: it does not reflect to the
   * `checked` content attribute and does not fire a `change` event, so it can
   * retrigger neither the observer below nor the listener above.
   */
  function restoreAgreement(surface) {
    if (!agreed[surface.agreement]) return;
    const checkbox = document.getElementById(surface.checkbox);
    if (checkbox && !checkbox.checked) checkbox.checked = true;
  }

  /**
   * Re-apply after a re-render, so the shopper keeps SEEING their tick.
   *
   * Both roots observed here sit ABOVE every subtree that gets replaced:
   *
   * - <cart-drawer> outlives every drawer refresh. cart.js's
   *   CartItems.updateQuantity() and cart-drawer.js's renderContents() replace
   *   .drawer__inner / #CartDrawer inside it, and the app script in
   *   layout/theme.liquid replaces its innerHTML on `cart:refresh` — the
   *   element itself is never the thing swapped.
   * - <main id="MainContent"> outlives every cart-page refresh. The agreement
   *   sits in .cart__ctas, a sibling of the .js-contents block cart.js
   *   replaces (sections/main-cart-footer.liquid:46 vs :107-109), so that
   *   replacement never reaches it. What does is the app script in
   *   layout/theme.liquid, which replaces the whole cart-footer section
   *   wrapper's contents on `cart:refresh`. It lives inside <main>, whose id
   *   is stock Dawn and stable, unlike the template--<id>__cart-footer
   *   section id the app script hardcodes.
   *
   * This is a SECOND observer, separate from the one cart-drawer.js runs on
   * <cart-drawer>: that one exists for the drawer's own scroll/focus/gift-cue
   * restore, knows nothing about the agreement, and does not watch the cart
   * page at all — which this must. Reaching into it from here would couple two
   * unrelated concerns across two files to save one id lookup.
   *
   * childList/subtree only. `attributes` is deliberately omitted, matching the
   * drawer's observer; this callback writes no attributes, so it cannot feed
   * back on itself either way.
   */
  function restoreBothAgreements() {
    restoreAgreement(PAGE);
    restoreAgreement(DRAWER);
  }

  const agreementObserver = new MutationObserver(restoreBothAgreements);
  [document.querySelector('cart-drawer'), document.getElementById('MainContent')].forEach(function (root) {
    if (root) agreementObserver.observe(root, { childList: true, subtree: true });
  });

  /** True when this surface shows an agreement the shopper has not ticked. */
  function isBlocked(surface) {
    // Absent means domestic market: nothing to agree to, nothing to block.
    if (!document.getElementById(surface.agreement)) return false;
    const checkbox = document.getElementById(surface.checkbox);
    return !checkbox || !checkbox.checked;
  }

  function showValidationError(surface) {
    const agreement = document.getElementById(surface.agreement);
    if (!agreement || !agreement.parentNode) return;

    const existingError = document.querySelector('.shipping-error');
    if (existingError) existingError.remove();

    const errorDiv = document.createElement('div');
    errorDiv.className = 'shipping-error';
    errorDiv.style.cssText = `
      background: #ffebee;
      border: 1px solid #f44336;
      color: #c62828;
      padding: 12px;
      border-radius: 4px;
      margin: 10px 0;
      font-size: 14px;
      font-weight: 500;
      text-align: center;
    `;
    errorDiv.textContent = '⚠️ Please agree to the international shipping terms before proceeding to checkout.';

    // Anchored to the agreement of the surface the shopper is looking at, so
    // the message lands inside the open drawer rather than on the page behind
    // it.
    agreement.parentNode.insertBefore(errorDiv, agreement);
    errorDiv.scrollIntoView({ behavior: 'smooth', block: 'center' });

    setTimeout(function () {
      if (errorDiv.parentNode) errorDiv.remove();
    }, 7000);
  }

  function block(event, element) {
    const surface = surfaceFor(element);
    // Reconcile the checkbox to the recorded agreement HERE rather than trust
    // the observer to have already run. gift-manager.js re-fires checkout from
    // a promise continuation queued right after the refresh that replaced the
    // checkbox, and whether that continuation or the observer's microtask wins
    // is not a race this guard should be settling. Restoring inline makes the
    // guard's answer independent of it.
    restoreAgreement(surface);
    if (!isBlocked(surface)) return;
    event.preventDefault();
    // Capture phase plus stopPropagation, so gift-manager.js's own submit
    // handler never starts its reconcile-and-re-fire for a checkout we are
    // refusing.
    event.stopPropagation();
    showValidationError(surface);
  }

  document.addEventListener(
    'click',
    function (event) {
      const target = event.target;
      const button = target && target.closest ? target.closest(CHECKOUT_SELECTOR) : null;
      if (!button) return;
      block(event, button);
    },
    true
  );

  // Covers submits that did not come from a click (Enter inside the form).
  // Keyed on the submitter's name rather than the old form[action*="cart"]
  // match, which also caught the gift picker's /cart/add forms and showed an
  // international shopper a shipping-terms error for adding a free gift.
  document.addEventListener(
    'submit',
    function (event) {
      const submitter = event.submitter;
      if (!submitter || submitter.name !== 'checkout') return;
      block(event, submitter);
    },
    true
  );
});
